import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';

import { SSUSelect } from 'components/SSUFilter/SSUSelect';

import { PatientDatabaseApi } from '../../../../../api';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { onRequestError } from '../../../../../services/handlers';

import {
  buildPushPayload,
  getSites,
  getStudies,
  mapStudySitesToSsuOptions,
  studyFilterFunction
} from './PushToPreScreeningModalService';

import './PushToPreScreeningModal.scss';

export const PushToPreScreeningModal = ({
  modalBox,
  selectedPatientIds,
  onPush,
  cachedStudySites = null,
  onStudySitesLoaded
}) => {
  const [studySites, setStudySites] = useState(cachedStudySites || []);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isLoading, setIsLoading] = useState(cachedStudySites === null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (cachedStudySites !== null) {
      setStudySites(cachedStudySites);
      setIsLoading(false);
      return;
    }

    PatientDatabaseApi.getActiveAndEnrollingStudySites()
      .then(
        ({ data }) => {
          const mappedStudySites = mapStudySitesToSsuOptions(data);
          setStudySites(mappedStudySites);
          onStudySitesLoaded?.(mappedStudySites);
        },
        error => {
          setStudySites([]);
          onRequestError(error);
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [cachedStudySites, onStudySitesLoaded]);

  const studies = useMemo(() => getStudies(studySites), [studySites]);
  const sites = useMemo(() => getSites(studySites, selectedStudy), [selectedStudy, studySites]);
  const pushDisabled = isLoading || isSubmitting || !selectedStudy || !selectedSite;

  const handleStudyChange = study => {
    setSelectedStudy(study);
    setSelectedSite(null);
  };

  const handlePush = () => {
    if (pushDisabled) {
      return;
    }

    const payload = buildPushPayload(selectedPatientIds, selectedStudy, selectedSite, studySites);

    setIsSubmitting(true);
    Promise.resolve(onPush(payload))
      .then(() => {
        modalBox.close();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <ModalBoxes.Body>
        <div className="push-to-pre-screening-modal-content">
          <SSUSelect
            studies={studies}
            sites={sites}
            handleStudyChange={handleStudyChange}
            handleSiteChange={setSelectedSite}
            initialStudy={selectedStudy}
            initialSite={selectedSite}
            studyCustomSelectedValueTemplateFunction={studySelectedValueTemplate}
            studyCustomFilterFunction={studyFilterFunction}
            studyCustomOptionTemplateFunction={studyOptionTemplate}
          />
        </div>
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <Button variant="text" onClick={modalBox.close} data-testid="push-to-prescreening-cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePush}
          disabled={pushDisabled}
          data-testid="push-to-prescreening-confirm-btn"
          sx={{ ml: '15px' }}
        >
          Push
        </Button>
      </ModalBoxes.Footer>
    </>
  );
};

function studyOptionTemplate(option) {
  return (
    <div className="push-to-pre-screening-study-option">
      <div className="push-to-pre-screening-study-option-pcn">{option?.projectCode || '-'}</div>
      <div>{option?.studyName}</div>
    </div>
  );
}

function studySelectedValueTemplate(studies) {
  const selectedStudy = studies[0];
  if (!selectedStudy) {
    return null;
  }

  return selectedStudy.projectCode ? (
    <span className="push-to-pre-screening-study-selected-value">
      <span>{selectedStudy.studyName}</span>
      <span className="push-to-pre-screening-study-selected-pcn"> ({selectedStudy.projectCode})</span>
    </span>
  ) : (
    selectedStudy.studyName
  );
}
